<template>
  <div v-loading="loading" class="nx-page nx-report-page planned-vehicle-monitor">
    <nx-report-header title="计划来车监控看板">
      <div class="pvm-header-actions">
        <span class="pvm-refresh-time">更新时间：{{ summary.lastRefreshTime || '--' }}</span>
        <el-switch v-model="autoRefresh" active-text="自动刷新" @change="onAutoRefreshChange" />
        <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-form :inline="true" class="pvm-filter-bar" size="small" @submit.native.prevent>
      <el-form-item label="计划日期">
        <el-date-picker
          v-model="queryDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="选择日期"
          style="width: 150px"
          @change="loadData"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="loadData">查询</el-button>
      </el-form-item>
    </el-form>

    <div class="pvm-kpi-row">
      <div class="pvm-kpi-card">
        <div class="pvm-kpi-label">当日计划总量</div>
        <div class="pvm-kpi-value">{{ fmtTon(summary.planTotalWeight) }}</div>
        <div class="pvm-kpi-unit">吨 / {{ summary.planCount || 0 }} 条计划</div>
      </div>
      <div class="pvm-kpi-card">
        <div class="pvm-kpi-label">实际完成量</div>
        <div class="pvm-kpi-value is-success">{{ fmtTon(summary.actualCompleteWeight) }}</div>
        <div class="pvm-kpi-unit">吨</div>
      </div>
      <div class="pvm-kpi-card">
        <div class="pvm-kpi-label">完成率</div>
        <div class="pvm-kpi-value">{{ fmtPct(summary.completeRatePercent) }}</div>
        <div class="pvm-progress">
          <i :style="{ width: barWidth(summary.completeRatePercent) }" />
        </div>
      </div>
      <div class="pvm-kpi-card">
        <div class="pvm-kpi-label">进度状态</div>
        <div class="pvm-kpi-status">
          <el-tag :type="tagType(summary.progressStatusKey)" effect="dark">{{ summary.progressStatusLabel || '--' }}</el-tag>
        </div>
        <div class="pvm-kpi-unit">时间进度 {{ fmtPct(summary.timeProgressPercent) }}</div>
      </div>
    </div>

    <div class="pvm-chart-row">
      <div class="pvm-chart-card">
        <div class="pvm-card-head">
          <span>物料类型发运对比</span>
          <small>计划量 / 实际完成量</small>
        </div>
        <div ref="materialChart" class="pvm-chart-box" />
      </div>
      <div class="pvm-chart-card is-narrow">
        <div class="pvm-card-head">
          <span>进度状态分布</span>
          <small>计划条数</small>
        </div>
        <div ref="statusChart" class="pvm-chart-box" />
      </div>
    </div>

    <div class="pvm-section">
      <div class="pvm-card-head">
        <span>发运计划执行明细</span>
        <small>{{ planRows.length }} 条</small>
      </div>
      <el-table :data="planRows" border stripe size="small" max-height="480" style="width: 100%">
        <el-table-column prop="planNo" label="计划编号" min-width="140" show-overflow-tooltip />
        <el-table-column prop="planName" label="计划名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="custName" label="货主名称" min-width="120" show-overflow-tooltip />
        <el-table-column prop="cargoName" label="物料类型" min-width="110" show-overflow-tooltip />
        <el-table-column label="计划区间" width="170">
          <template slot-scope="{ row }">
            <span class="pvm-date-range">{{ row.intervalStartStr || '--' }} ~ {{ row.intervalEndStr || '--' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="planTotalWeight" label="计划量(吨)" width="118" align="right" :formatter="fmtTonCol" />
        <el-table-column prop="actualCompleteWeight" label="完成量(吨)" width="118" align="right" :formatter="fmtTonCol" />
        <el-table-column label="完成率" width="130">
          <template slot-scope="{ row }">
            <span>{{ fmtPct(row.completeRatePercent) }}</span>
            <div class="pvm-table-progress">
              <i :style="{ width: barWidth(row.completeRatePercent) }" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="车数 计划/完成/在途" width="145" align="center">
          <template slot-scope="{ row }">
            {{ row.planTruckCount || 0 }} / {{ row.completeTruckCount || 0 }} / {{ row.executingTruckCount || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="进度状态" width="108">
          <template slot-scope="{ row }">
            <el-tag :type="tagType(row.progressStatusKey)" size="small">{{ row.progressStatusLabel || '--' }}</el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchPlannedVehicleMonitor } from './api'
import './index.scss'

export default {
  name: 'KanbanPlannedVehicleMonitor',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      queryDate: moment().format('YYYY-MM-DD'),
      result: null,
      autoRefresh: true,
      timer: null,
      chartInstances: []
    }
  },
  computed: {
    summary() {
      const s = (this.result && this.result.summary) || {}
      return {
        ...s,
        lastRefreshTime: this.result && this.result.lastRefreshTime
      }
    },
    materialComparisons() {
      return (this.result && this.result.materialComparisons) || []
    },
    statusStats() {
      return (this.result && this.result.statusStats) || []
    },
    planRows() {
      return (this.result && this.result.planRows) || []
    }
  },
  mounted() {
    this.loadData()
    this.startTimer()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    this.stopTimer()
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    async loadData() {
      if (!this.queryDate) {
        this.queryDate = moment().format('YYYY-MM-DD')
      }
      this.loading = true
      try {
        this.result = await fetchPlannedVehicleMonitor({
          queryDate: this.queryDate,
          businessType: 2
        })
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    renderCharts() {
      this.disposeCharts()
      this.renderMaterialChart()
      this.renderStatusChart()
    },
    renderMaterialChart() {
      const el = this.$refs.materialChart
      if (!el) return
      const rows = this.materialComparisons.length
        ? this.materialComparisons
        : [{ cargoName: '暂无数据', planWeight: 0, actualWeight: 0 }]
      const chart = echarts.init(el)
      chart.setOption({
        color: ['#2563eb', '#16a34a'],
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { top: 0, right: 6, data: ['计划量', '完成量'] },
        grid: { left: 46, right: 18, top: 46, bottom: 44 },
        xAxis: {
          type: 'category',
          data: rows.map((item) => item.cargoName || '未知物料'),
          axisLabel: { interval: 0, rotate: rows.length > 4 ? 28 : 0 }
        },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '计划量', type: 'bar', barMaxWidth: 26, data: rows.map((item) => Number(item.planWeight) || 0) },
          { name: '完成量', type: 'bar', barMaxWidth: 26, data: rows.map((item) => Number(item.actualWeight) || 0) }
        ]
      })
      this.chartInstances.push(chart)
    },
    renderStatusChart() {
      const el = this.$refs.statusChart
      if (!el) return
      const data = this.statusStats
        .filter((item) => (Number(item.count) || 0) > 0)
        .map((item) => ({ name: item.statusLabel, value: item.count }))
      const chart = echarts.init(el)
      chart.setOption({
        color: ['#94a3b8', '#3b82f6', '#22c55e', '#f59e0b', '#10b981'],
        tooltip: { trigger: 'item' },
        series: [
          {
            name: '状态',
            type: 'pie',
            radius: ['48%', '72%'],
            center: ['50%', '52%'],
            minAngle: 8,
            label: { formatter: '{b}\n{c}' },
            data: data.length ? data : [{ name: '暂无数据', value: 1, itemStyle: { color: '#cbd5e1' } }]
          }
        ]
      })
      this.chartInstances.push(chart)
    },
    disposeCharts() {
      this.chartInstances.forEach((chart) => chart && chart.dispose())
      this.chartInstances = []
    },
    onResize() {
      this.chartInstances.forEach((chart) => chart && chart.resize())
    },
    onAutoRefreshChange() {
      if (this.autoRefresh) {
        this.startTimer()
      } else {
        this.stopTimer()
      }
    },
    startTimer() {
      this.stopTimer()
      if (this.autoRefresh) {
        this.timer = window.setInterval(() => this.loadData(), 30000)
      }
    },
    stopTimer() {
      if (this.timer) {
        window.clearInterval(this.timer)
        this.timer = null
      }
    },
    fmtTon(value) {
      if (value == null || value === '') return '--'
      const n = Number(value)
      if (Number.isNaN(n)) return '--'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    fmtTonCol(row, column, value) {
      return this.fmtTon(value)
    },
    fmtPct(value) {
      if (value == null || value === '') return '--'
      const n = Number(value)
      if (Number.isNaN(n)) return '--'
      return n.toFixed(1) + '%'
    },
    barWidth(value) {
      const n = Math.min(100, Math.max(0, Number(value) || 0))
      return n + '%'
    },
    tagType(key) {
      if (key === 'done' || key === 'normal') return 'success'
      if (key === 'late') return 'danger'
      if (key === 'in_progress') return 'primary'
      return 'info'
    }
  }
}
</script>
